import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import PurchaseTableRow from './PurchaseTableRow'
import { Purchase, PurchaseStage } from '../../../../types'
import LoadingIcon from '../../../../../../../components/LoadingIcon'
import { CurrencyList } from '../../../types'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

type Props = {
    theme: string
    purchaseGuid: string
    purchaseRegNumber: string
    isActive: boolean
    currencyList: CurrencyList[]
    // contractPrice: number
    dataReloadToken: number
    purchaseVersion: number
    purchaseYear: number | null
    purchaseStage: string
}

/** форматирует числа “1 234,56” и обрабатывает пустые значения */
const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-'
    const parsedNum = typeof num === 'number' ? num : parseFloat(num)
    if (isNaN(parsedNum)) return '-'
    return parsedNum
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',')
}

const PurchaseTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    const [purchases, setPurchases] = useState<Purchase[]>([])
    const [purchasesLoadedGuid, setPurchasesLoadedGuid] = useState<
        string | null
    >(null)
    const [purchasesLoading, setPurchasesLoading] = useState(false)
    const [purchasesError, setPurchasesError] = useState<string | null>(null)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [
        deletePurchasePositionRowsPopup,
        setDeletePurchasePositionRowsPopup,
    ] = useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deleteRows, setDeleteRows] = useState<Purchase[]>([])

    // для отмены текущих фетчей
    const listAbortRef = useRef<AbortController | null>(null)
    const totalsAbortRef = useRef<AbortController | null>(null)

    const [updateFromEisPopup, setUpdateFromEisPopup] = useState<boolean>(false)
    const [isUpdatingFromEis, setUpdatingFromEis] = useState<boolean>(false)
    const [popupMessage, setPopupMessage] = useState('')
    const [forceUpdateUrl, setForceUpdateUrl] = useState<string | null>(null)

    const [updateErrorPopup, setUpdateErrorPopup] = useState<boolean>(false)
    const [updateErrorText, setUpdateErrorText] = useState<string>('')

    const fetchPurchasesRef = useRef<
        ((force?: boolean) => Promise<void>) | null
    >(null)

    // ====== loaders ======
    const fetchPurchases = useCallback(
        async (force = false) => {
            if (!props.purchaseGuid) return
            if (!force && purchasesLoadedGuid === props.purchaseGuid) return

            // важно: если форсим, сбросим метку
            if (force) setPurchasesLoadedGuid(null)

            if (listAbortRef.current) listAbortRef.current.abort()
            const controller = new AbortController()
            listAbortRef.current = controller

            try {
                setPurchasesLoading(true)
                setPurchasesError(null)

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/backend/v1/purchases/purchase_reg_number/${props.purchaseRegNumber}`,
                    {
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        signal: controller.signal,
                    }
                )
                if (!res.ok)
                    throw new Error('Failed to fetch purchase positions')
                const data: Purchase[] = await res.json()
                setPurchases(data ?? [])
                setPurchasesLoadedGuid(props.purchaseGuid)

                // если стоял Select All — пересинхронизируем выбранные
                if (selectAll) {
                    setSelectedItems((data ?? []).map((i) => i.guid))
                } else {
                    // почистим выбранные, которых уже нет
                    setSelectedItems((prev) =>
                        prev.filter((id) =>
                            (data ?? []).some((d) => d.guid === id)
                        )
                    )
                }
            } catch (e: any) {
                if (e?.name !== 'AbortError') {
                    setPurchasesError(e?.message ?? 'Ошибка загрузки договоров')
                }
            } finally {
                setPurchasesLoading(false)
            }
        },
        [
            props.purchaseGuid,
            props.purchaseRegNumber,
            purchasesLoadedGuid,
            selectAll,
        ]
    )

    useEffect(() => {
        fetchPurchasesRef.current = fetchPurchases
    }, [fetchPurchases])

    useEffect(() => {
        if (!props.isActive) return
        fetchPurchasesRef.current?.(true)
    }, [props.dataReloadToken, props.isActive])

    useEffect(() => {
        fetchPurchases()
        return () => {
            listAbortRef.current?.abort()
        }
    }, [fetchPurchases])

    // ====== helpers for stage/status (поддержим и строковые значения, и enum) ======
    const isPlacedInEis = (p: Purchase) =>
        p.purchase_stage === PurchaseStage.START

    const isEditingStage = (p: Purchase) =>
        p.purchase_stage === 'Редактирование' // при желании добавь enum, если он есть

    const isDraftStatus = (p: Purchase) => p.purchase_status === 'Черновик' // при желании добавь enum, если он есть

    const isReadyStage = (p: Purchase) =>
        p.purchase_stage === 'Подготовка к размещению' // при желании добавь enum, если он есть

    const isReadyDraftStatus = (p: Purchase) =>
        p.purchase_status === 'Подготовка' // при желании добавь enum, если он есть

    const isDeletable = (p: Purchase) =>
        (isEditingStage(p) && isDraftStatus(p)) ||
        (isReadyStage(p) && isReadyDraftStatus(p))

    const isUpdatable = (p: Purchase) =>
        p.purchase_stage === 'Отправка в ЕИС' &&
        p.purchase_status === 'Отправлено в ЕИС' // при желании добавь enum, если он есть

    // ====== derived UI states ======
    const canAddPurchase = purchases.every(isPlacedInEis) // если пусто -> true

    const canUpdatePurchase = !purchases.every(isUpdatable) // если пусто -> true
    const notPlacedPurchaseGuid: string | null =
        purchases.find((p) => !isPlacedInEis(p))?.guid ?? null

    const selectedPurchases = purchases.filter((p) =>
        selectedItems.includes(p.guid)
    )

    // если хотя бы один guid не найден в purchases -> запрещаем удаление (на всякий случай)
    const canDeleteSelected =
        selectedItems.length > 0 &&
        selectedPurchases.length === selectedItems.length &&
        selectedPurchases.every(isDeletable)

    const handleUpdateFromEisPopup = () => {
        setUpdateFromEisPopup(!updateFromEisPopup)
        setPopupMessage('')
    }

    const updatePurchaseFromEis = async (
        purchase_guid: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchases/from-eis?purchase_guid=${purchase_guid}`

        setUpdatingFromEis(true)

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            // Успешный апдейт — просто переходим на страницу редактирования
            if (response.ok) {
                const data = await response.json().catch(() => null)

                navigate(
                    `/hub/purchase/${data?.registration_number || props.purchaseRegNumber}?tab=Исполнение договора`
                )
                return
            }

            // --- обработка ошибок ---
            let errorData: any = null
            try {
                errorData = await response.json()
            } catch (e) {
                console.error('Failed to parse JSON from response:', e)
            }

            const detail: string =
                (errorData && (errorData.detail || errorData.message)) ||
                `Unexpected error. Status: ${response.status}`

            // 1) спец-кейс: уже обновлено
            if (detail.includes('уже обновлено')) {
                setPopupMessage(
                    `${detail}\nВыполнить обновление данных из ЕИС все равно?`
                )
                setForceUpdateUrl(
                    process.env.REACT_APP_API_URL +
                        `/api/backend/v1/contract_from_eis?purchase_guid=${purchase_guid}&force_update=true`
                )
                setUpdateFromEisPopup(true)
                return
            }

            // 2) любые другие ошибки — попап с textarea
            setUpdateErrorText(detail)
            setUpdateErrorPopup(true)
        } catch (error: any) {
            console.error('Error while updating purchase from EIS:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
        }
    }

    const handleForceUpdate = async () => {
        if (!forceUpdateUrl) return

        setUpdatingFromEis(true)
        try {
            const response = await fetch(forceUpdateUrl, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                let errorData: any = null
                try {
                    errorData = await response.json()
                } catch (e) {
                    console.error('Failed to parse JSON from response:', e)
                }

                const detail: string =
                    (errorData && (errorData.detail || errorData.message)) ||
                    `Failed to force update purchase. Status: ${response.status}`

                setUpdateErrorText(detail)
                setUpdateErrorPopup(true)
                return
            }

            // Успешный принудительный апдейт
            navigate(`/hub/purchase/${props.purchaseRegNumber}?tab=Договор`)
        } catch (error: any) {
            console.error('Error while forcing update purchase:', error)
            const msg =
                error?.message ||
                'Неизвестная ошибка при принудительном обновлении данных из ЕИС'
            setUpdateErrorText(msg)
            setUpdateErrorPopup(true)
        } finally {
            setUpdatingFromEis(false)
            setUpdateFromEisPopup(false)
        }
    }

    const handleUpdateErrorPopup = () => {
        const needReload = updateErrorText
            ?.toLowerCase()
            .includes('договор переведен')

        setUpdateErrorPopup(false)
        setUpdateErrorText('')

        if (needReload) {
            fetchPurchases(true)
        }
    }

    return (
        <>
            <fieldset disabled={false}>
                <div
                    className={`flex flex-col w-full min-w-fit bg-[#e4e6e8] dark:bg-[#242225] rounded`}
                >
                    {/* toolbar */}
                    <div className="flex flex-row gap-1 p-1 h-11 rounded-t text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center">
                            <div className="w-6 h-6 grid rounded-full place-items-center cursor-pointer hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <HoverTooltip tooltipText="Выбрать все">
                                    <input
                                        type="checkbox"
                                        className="p-2 cursor-pointer"
                                        checked={selectAll}
                                        readOnly
                                    />
                                </HoverTooltip>
                            </div>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Добавить */}
                            <button
                                disabled={!canAddPurchase}
                                className={`w-6 h-6 grid rounded-full place-items-center ${
                                    canAddPurchase
                                        ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]'
                                        : ''
                                }`}
                            >
                                <HoverTooltip tooltipText="Создать изменение">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                canAddPurchase
                                                    ? props.theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                    : '#8A8A8A'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-8 gap-1 items-center justify-center text-center">
                            {/* Удалить */}
                            <button
                                disabled={!canDeleteSelected}
                                className={`flex w-6 h-6 rounded-full items-center justify-center ${
                                    canDeleteSelected
                                        ? 'hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]'
                                        : ''
                                }`}
                            >
                                <HoverTooltip tooltipText="Удалить">
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="trash-alt"
                                    >
                                        <path
                                            fill={`${canDeleteSelected ? (props.theme == 'dark' ? '#FFFFFF' : '#181818') : '#8A8A8A'} `}
                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                        ></path>
                                    </svg>
                                </HoverTooltip>
                            </button>
                        </div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                        <div className="flex w-24 items-center justify-center text-center"></div>
                    </div>

                    {/* name */}
                    <div className="flex flex-row gap-1 h-10 p-1 items-center justify-center text-[10px] text-[#616173] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                        <div className="flex w-8 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex w-8 items-center justify-center text-center">
                            №
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Стадия
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Статус
                        </div>
                        <div className="flex flex-1 items-center justify-center text-center">
                            Наименование
                        </div>
                        <div className="flex w-24 items-center justify-center text-center">
                            Статус в ЕИС
                        </div>
                        <div className="flex w-20 items-center justify-center text-center">
                            Редакция
                        </div>
                        <div className="flex w-20 items-center justify-center text-center">
                            Дата размещения
                        </div>
                    </div>

                    {/* data */}
                    <div className="flex flex-col w-full gap-1 my-1 overflow-y custom-scrollbar">
                        {purchasesLoading ? (
                            <div className="flex w-full h-full items-center justify-center">
                                <LoadingIcon />
                            </div>
                        ) : (
                            purchases?.map((row, index) => (
                                <PurchaseTableRow
                                    key={row.guid}
                                    purchaseGuid={props.purchaseGuid}
                                    perf_number={index + 1}
                                    theme={props.theme}
                                    purchase={row}
                                    isActive={props.isActive}
                                    currencyList={props.currencyList}
                                    selected={selectedItems.includes(row.guid)}
                                    onDoubleClick={(purchase_guid) => {
                                        const purchase_action =
                                            row.purchase_stage ===
                                                PurchaseStage.EDIT ||
                                            row.purchase_stage ===
                                                PurchaseStage.READY_FOR_PLACEMENT ||
                                            row.purchase_stage ===
                                                PurchaseStage.PURCHASE_TO_EIS
                                                ? 'draft'
                                                : 'edit'
                                        navigate(
                                            `/hub/purchase/${props.purchaseRegNumber}/purchases/${purchase_action}/${purchase_guid}?tab=Извещение`,
                                            {
                                                state: {
                                                    from: 'purchase',
                                                    purchaseRegNumber:
                                                        props.purchaseRegNumber,
                                                },
                                            }
                                        )
                                    }}
                                />
                            ))
                        )}
                    </div>

                    {/* footer */}
                    <div className="flex flex-row  h-11 text-[11px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                        <div className="flex w-3 h-full p-1 items-center justify-center text-center"></div>
                        <div className="flex flex-1 gap-3 items-center justify-start">
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                    ${!canAddPurchase ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                `}
                                disabled={!canAddPurchase}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !canAddPurchase
                                                ? '#8A8A8A'
                                                : props.theme == 'dark'
                                                  ? '#FFFFFF'
                                                  : '#181818'
                                        }
                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                    ></path>
                                </svg>
                                Создать изменение
                            </button>
                            <button
                                className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${!canDeleteSelected ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] bg-[#F6F6F6] hover:bg-[#ed9cae] dark:bg-[#494849] dark:hover:bg-[#701f1f] dark:text-[#FFFFFF]'}
                                    `}
                                disabled={!canDeleteSelected}
                            >
                                <svg
                                    className="w-4 h-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="plus"
                                >
                                    <path
                                        fill={
                                            !canDeleteSelected
                                                ? '#8A8A8A'
                                                : props.theme == 'dark'
                                                  ? '#FFFFFF'
                                                  : '#181818'
                                        }
                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                    ></path>
                                </svg>
                                Удалить
                            </button>
                        </div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                        <div className="flex w-20 items-center justify-center text-center"></div>
                    </div>
                </div>
            </fieldset>

            {updateErrorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleUpdateErrorPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[600px] h-fit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-start gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Ошибки при обновлении данных из ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1 gap-2">
                            <div className="flex flex-col mt-2 space-y-1">
                                <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                    Внимание! При обновлении исполнения из ЕИС
                                    возникли ошибки:
                                </label>
                            </div>
                            <div className="flex flex-col mt-2 space-y-1">
                                <textarea
                                    className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                    value={updateErrorText}
                                    rows={21}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleUpdateErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default PurchaseTable
